/**
 * API Explorer list — browse agents and open their Chat Completions / Responses /
 * Assistants API surfaces.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`): `ShellHeader` +
 * `CordioAbpPagedDataTable`, with the grid owning paging, search and status tabs. The old
 * hand-rolled `DataTable` + `useEffect` that loaded a fixed page of 100 active agents is gone.
 *
 * ─── Column plan ────────────────────────────────────────────────────────────────────────────────
 * field             │ header   │ width │ cell type        │ filter
 * ──────────────────┼──────────┼───────┼──────────────────┼────────────────────────────────────────
 * name              │ Agent    │  28%  │ custom (click)   │ toolbar search (`searchTerm`)
 * slug              │ Slug     │  18%  │ text             │ popover text
 * category          │ Category │  16%  │ text             │ popover text
 * defaultModelName  │ Model    │  20%  │ custom           │ —
 * isActive          │ Status   │  18%  │ custom Chip      │ status tabs → synthetic `status`
 *
 * Row click and the eye action both navigate to `/api-explorer/:slug`. No delete here — this page
 * is a read-only gateway into per-agent API docs/data.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen } from 'lucide-react';

import {
  CordioAbpPagedDataTable,
  Chip,
  ShellHeader,
  Button,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { EmptyState } from '@coordio/ui/layout';

import { createCortexPagedDataSource } from '../components/data-display/cortexPagedDataSource';
import { agentService } from '../services/agentService';
import { useContextStore } from '../store/contextStore';
import { ROUTES } from '../utils/routes';

import type { AgentDefinitionDto } from '../types/agent';

import './ApiExplorerPage.scss';

const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
] as const;

const STATUS_TAB_PARAM_KEY = 'status';

/**
 * Maps grid query → `agentService.getAgents` filters. Synthetic `status` becomes `isActive`;
 * All drops the filter so the API returns every agent.
 */
function toAgentQuery(query: Record<string, unknown>): Record<string, unknown> {
  const { [STATUS_TAB_PARAM_KEY]: status, ...rest } = query;
  const mapped: Record<string, unknown> = { ...rest };

  if (status === 'true' || status === 'false') {
    mapped.isActive = status === 'true';
  }

  return mapped;
}

export const ApiExplorerPage = () => {
  const navigate = useNavigate();
  const currentTenant = useContextStore((s) => s.currentTenant);
  const tenantId = currentTenant?.id;

  /** Bumped only if we later add mutations; kept so the dataSource identity pattern stays standard. */
  const [listVersion] = useState(0);

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<AgentDefinitionDto>((query) => {
        if (!tenantId) throw new Error('No workspace selected');
        return agentService.getAgents(toAgentQuery(query));
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantId, listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<AgentDefinitionDto>>>(
    () => [
      {
        field: 'name',
        header: 'Agent',
        type: 'custom',
        width: '28%',
        clickable: true,
        customCell: (row) => (
          <div className="api-explorer-list__agent-cell">
            <span className="api-explorer-list__agent-name">{row.name}</span>
            {row.description ? (
              <span className="api-explorer-list__agent-description">{row.description}</span>
            ) : null}
          </div>
        ),
      },
      { field: 'slug', header: 'Slug', type: 'text', width: '18%' },
      { field: 'category', header: 'Category', type: 'text', width: '16%' },
      {
        field: 'defaultModelName',
        header: 'Model',
        type: 'custom',
        width: '20%',
        customCell: (row) => row.defaultModelName || '—',
      },
      {
        field: 'isActive',
        header: 'Status',
        type: 'custom',
        width: '18%',
        customCell: (row) => (
          <Chip label={row.isActive ? 'Active' : 'Inactive'} color={row.isActive ? 'green' : 'default'} />
        ),
      },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search agents by name' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Agent status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      filters: [{ key: 'category', label: 'Category', type: 'text', placeholder: 'Category' }],
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<AgentDefinitionDto>>>(
    () => [
      {
        icon: 'eye',
        tooltip: 'View API data',
        onClick: (row) => navigate(`${ROUTES.API_EXPLORER}/${row.slug}`),
      },
    ],
    [navigate],
  );

  if (!tenantId) {
    return (
      <div className="api-explorer-list" data-testid="api-explorer-form-page">
        <ShellHeader
          title="API Explorer"
          description="Browse Chat Completions, Responses, and Assistants API data per agent."
          data-testid="api-explorer-header"
        />
        <EmptyState
          title="No workspace selected"
          description="Choose a workspace from the user menu to explore agent APIs."
          data-testid="api-explorer-empty-state"
        />
      </div>
    );
  }

  return (
    <div className="api-explorer-list" data-testid="api-explorer-form-page">
      <ShellHeader
        title="API Explorer"
        description="Browse Chat Completions, Responses, and Assistants API data per agent."
        data-testid="api-explorer-header"
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => navigate(ROUTES.DEVELOPER_HELP)}
            data-testid="api-explorer-guides-btn"
          >
            <BookOpen className="h-4 w-4 shrink-0" aria-hidden />
            Developer Guides
          </Button>
        }
      />

      <div data-testid="api-explorer-list-grid">
        <CordioAbpPagedDataTable<AgentDefinitionDto>
          className="api-explorer-list__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(`${ROUTES.API_EXPLORER}/${row.slug}`)}
        />
      </div>
    </div>
  );
};
